/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable indent */
import React from "react";
import _ from "lodash/fp";
import styled from "styled-components";

import { colourNeutral7 } from "@ingka/variables/colours-css";
import CommercialMessage from "@ingka/commercial-message";
import Tooltip from "@ingka/tooltip";
import Button from "@ingka/button";
import chevronDown from "@ingka/ssr-icon/paths/chevron-down";
import chevronUp from "@ingka/ssr-icon/paths/chevron-up";

import { Schema } from "../../utils/types";
import * as helpers from "../../utils/helpers";
import { SchemaBox } from "../SchemaBox";
import { SchemaControls, SchemaArrayControls } from "./SchemaControls";
import { Input } from "../Input";
import { DeleteButton } from "../Buttons";
import { sortObjectByKey } from "../../../Schema/utils";
import { SrOnly } from "../../../styles";

type Props = {
	schema: Schema;
	schemakey?: string;
	isRequired?: boolean;
	onChangeKey?: (key: string) => void;
	onDelete?: (key: string) => void;
	onChange?: (schema: Schema) => void;
	setInvalidKeys?: React.Dispatch<React.SetStateAction<string[]>>;
	invalidKeys?: string[];
	schemaKeys?: string[];
	showAllProperties: boolean;
	setShowAllProperties?: (val: boolean) => void;
	hasChanged: boolean;
};

const StyledOptionLabel = styled.label`
	display: flex;
	gap: 0.5rem;
	align-items: center;
`;

interface CollapsibleWrapperProps {
	isCollapsed: boolean;
}

const CollapsibleWrapper = styled.div<CollapsibleWrapperProps>`
	display: ${({ isCollapsed }) => (isCollapsed ? "none" : "block")};
`;

const ControllsWrapper = styled.div`
	display: flex;
	gap: 1rem;
	align-items: center;
`;

const RequiredIndicatorWrapper = styled.div`
	margin-bottom: 1.25rem;
`;

const ExpandButtonWrapper = styled.div`
	padding-bottom: 1.25rem;
`;

/** Visually-hidden live region ref announcer. */
function useAnnouncer() {
	const liveRegionRef = React.useRef<HTMLDivElement | null>(null);

	const announce = React.useCallback((message: string) => {
		if (!liveRegionRef.current) return;
		liveRegionRef.current.textContent = "";
		window.requestAnimationFrame(() => {
			if (liveRegionRef.current) {
				liveRegionRef.current.textContent = message;
			}
		});
	}, []);

	return { liveRegionRef, announce };
}

const LiveRegion = React.forwardRef<HTMLDivElement>((_, ref) => (
	<div
		ref={ref}
		aria-live="polite"
		aria-atomic="true"
		style={{
			position: "absolute",
			width: 1,
			height: 1,
			margin: -1,
			padding: 0,
			overflow: "hidden",
			clip: "rect(0 0 0 0)",
			border: 0,
		}}
	/>
));
LiveRegion.displayName = "LiveRegion";

const SchemaCreator: React.FunctionComponent<Props> = ({
	schema,
	onChangeKey = _.noop,
	onChange = _.noop,
	onDelete = _.noop,
	schemakey = "root",
	isRequired,
	setInvalidKeys,
	invalidKeys,
	schemaKeys,
	showAllProperties,
	setShowAllProperties,
	hasChanged,
}: Props) => {
	const [isCollapsed, setIsCollapsed] = React.useState<boolean>(schemakey !== "root");
	const { liveRegionRef, announce } = useAnnouncer();

	React.useEffect(() => {
		if (schemakey !== "root") {
			setIsCollapsed(!showAllProperties);
		}
	}, [schemakey, showAllProperties]);

	// Sort properties when nothing has changed
	React.useEffect(() => {
		if (
			schema != null &&
			helpers.isSchemaObject(schema) &&
			schema.properties &&
			Object.keys((schema.properties as Record<string, unknown>) ?? {}).length > 1 &&
			!hasChanged
		) {
			schema.properties = sortObjectByKey(schema.properties);
		}
	}, [hasChanged, schema]);

	const isSchemaHaveMultipleProperties = React.useMemo(
		() =>
			schema != null &&
			helpers.isSchemaObject(schema) &&
			Object.keys((schema.properties as Record<string, unknown>) ?? {}).length > 1,
		[schema]
	);

	const isArray = React.useMemo(() => schema != null && helpers.isSchemaArray(schema), [schema]);

	const schemaPropertyKeys: string[] = React.useMemo(
		() => (helpers.isSchemaObject(schema) ? helpers.getKeysInSchema(schema) : schemaKeys ?? []),
		[schema, schemaKeys]
	);

	const handleAddProperty = React.useCallback(() => {
		setIsCollapsed(false);
		const updatedSchema = helpers.addSchemaProperty(schema);
		onChange(updatedSchema);

		const label = schemakey === "root" ? "root object" : `${schemakey} object`;
		announce(`New property added under ${label}`);
	}, [schema, onChange, schemakey, announce]);

	const handleAddSelectOption = React.useCallback(() => {
		setIsCollapsed(false);
		const updatedSchema = helpers.addSelectOptions(schema);
		onChange(updatedSchema);

		const label = schemakey === "root" ? "root object" : `${schemakey} object`;
		announce(`New option added under ${label}`);
	}, [schema, onChange, schemakey, announce]);

	const onAdd = helpers.isSchemaObject(schema)
		? handleAddProperty
		: helpers.isSelectSchema(schema)
		? handleAddSelectOption
		: undefined;

	const onCollapse =
		helpers.isSchemaObject(schema) || helpers.isSchemaArray(schema) || helpers.isSelectSchema(schema)
			? () => setIsCollapsed((c) => !c)
			: undefined;

	return (
		<div
			role="group"
			aria-labelledby={`schema-label-${schemakey}`}
			aria-describedby={isRequired ? `required-indicator-${schemakey}` : undefined}
		>
			<LiveRegion ref={liveRegionRef} />

			<ControllsWrapper>
				<SchemaControls
					schema={schema}
					schemakey={schemakey}
					isCollapsed={isCollapsed}
					onChangeKey={schemakey !== "root" ? onChangeKey : undefined}
					onAdd={onAdd}
					onAddDashboard={helpers.isSchemaObject(schema) ? undefined : () => helpers.addDashboardOptions(schema)}
					onCollapse={onCollapse}
					onDelete={schemakey !== "root" ? () => onDelete(schemakey) : undefined}
					onChange={onChange}
					invalidKeys={invalidKeys}
					setInvalidKeys={setInvalidKeys}
					schemaKeys={schemaPropertyKeys}
					isRequired={isRequired}
				/>

				{schemakey === "root" && (
					<ExpandButtonWrapper>
						<Tooltip
							interactiveCustomTrigger
							position="top"
							tooltipText={`${!showAllProperties ? "Expand" : "Collapse"} all properties`}
						>
							<Button
								aria-label={`${!showAllProperties ? "Expand" : "Collapse"} all properties`}
								data-testid="expandAll"
								size="small"
								iconPosition="trailing"
								text={!showAllProperties ? "Expand" : "Collapse"}
								ssrIcon={!showAllProperties ? chevronDown : chevronUp}
								onClick={() => setShowAllProperties && setShowAllProperties(!showAllProperties)}
								type="secondary"
							/>
						</Tooltip>
					</ExpandButtonWrapper>
				)}

				{isRequired && (
					<>
						<RequiredIndicatorWrapper>
							<CommercialMessage subtle message="Required" aria-hidden={true} />
						</RequiredIndicatorWrapper>
						<SrOnly id={`required-indicator-${schemakey}`}>Required field</SrOnly>
					</>
				)}
			</ControllsWrapper>

			<CollapsibleWrapper isCollapsed={isCollapsed}>
				{helpers.isSchemaObject(schema) && helpers.hasSchemaProperties(schema) && (
					<SchemaBox height={isSchemaHaveMultipleProperties ? undefined : 22} isRoot={schemakey === "root"}>
						<SchemaObjectProperties
							onChangeKey={(oldkey, newkey) => onChange(helpers.renameSchemaProperty(oldkey, newkey, schema))}
							onDelete={(key) => onChange(helpers.deleteSchemaProperty(key)(schema))}
							onChange={(key, s) => onChange(helpers.setSchemaProperty(key)(s, schema))}
							schema={schema}
							invalidKeys={invalidKeys}
							setInvalidKeys={setInvalidKeys}
							schemaKeys={schemaPropertyKeys}
							showAllProperties={showAllProperties}
							hasChanged={hasChanged}
						/>
					</SchemaBox>
				)}

				{helpers.isSchemaArray(schema) && (
					<SchemaBox height={isArray ? 20 : undefined}>
						<SchemaArrayItems
							schemakey={schemakey}
							schema={helpers.getSchemaItems(schema)}
							onChange={(s) => onChange(helpers.setSchemaItems(s, schema))}
							invalidKeys={invalidKeys}
							setInvalidKeys={setInvalidKeys}
							showAllProperties={showAllProperties}
							hasChanged={hasChanged}
						/>
					</SchemaBox>
				)}

				{helpers.isSelectSchema(schema) && (
					<SchemaBox>
						<SchemaSelectOptions
							schemakey={schemakey}
							schema={schema}
							invalidKeys={invalidKeys}
							setInvalidKeys={setInvalidKeys}
							onChange={() => {
								onChange(helpers.setSchemaTypeAndRemoveWrongFields("oneOf", schema));
							}}
						/>
					</SchemaBox>
				)}
			</CollapsibleWrapper>
		</div>
	);
};

export default SchemaCreator;

interface ArrayProps {
	schema: Schema;
	onChange: (schema: Schema) => void;
	schemakey?: string;
	setInvalidKeys?: React.Dispatch<React.SetStateAction<string[]>>;
	invalidKeys?: string[];
	showAllProperties: boolean;
	hasChanged: boolean;
}

const SchemaArrayItemsObjectWrapper = styled.div`
	margin-top: 0.5rem;
`;

const SchemaArrayControlsWrapper = styled.div`
	margin-left: 2.5rem;
`;

const SchemaArrayItems: React.FC<ArrayProps> = ({
	schema,
	onChange,
	schemakey,
	setInvalidKeys,
	invalidKeys,
	showAllProperties,
	hasChanged,
}) => {
	const [isCollapsed, setIsCollapsed] = React.useState<boolean>(true);
	const { liveRegionRef, announce } = useAnnouncer();

	React.useEffect(() => {
		setIsCollapsed(!showAllProperties);
	}, [showAllProperties]);

	const handleAddProperty = React.useCallback(() => {
		setIsCollapsed(false);
		const updatedSchema = helpers.addSchemaProperty(schema);
		onChange(updatedSchema);

		const label = schemakey ? `${schemakey} array item` : "array item";
		announce(`New property added under ${label}`);
	}, [schema, onChange, schemakey, announce]);

	const handleAddSelectOption = React.useCallback(() => {
		setIsCollapsed(false);
		const updatedSchema = helpers.addSelectOptions(schema);
		onChange(updatedSchema);

		const label = schemakey ? `${schemakey} array item` : "array item";
		announce(`New option added under ${label}`);
	}, [schema, onChange, schemakey, announce]);

	const onAdd = helpers.isSchemaObject(schema)
		? handleAddProperty
		: helpers.isSelectSchema(schema)
		? handleAddSelectOption
		: undefined;

	const onCollapse =
		helpers.isSchemaObject(schema) || helpers.isSchemaArray(schema) || helpers.isSelectSchema(schema)
			? () => setIsCollapsed((c) => !c)
			: undefined;

	return (
		<SchemaArrayControlsWrapper>
			<LiveRegion ref={liveRegionRef} />

			<div>
				<SchemaArrayControls
					schemakey={schemakey}
					schema={schema}
					onChange={onChange}
					onAdd={onAdd}
					onCollapse={onCollapse}
					isCollapsed={isCollapsed}
				/>
			</div>

			<CollapsibleWrapper isCollapsed={isCollapsed}>
				{helpers.isSchemaObject(schema) && helpers.hasSchemaProperties(schema) && (
					<SchemaBox height={22}>
						<SchemaArrayItemsObjectWrapper>
							<SchemaObjectProperties
								onChangeKey={(oldkey, newkey) => onChange(helpers.renameSchemaProperty(oldkey, newkey, schema))}
								onDelete={(key) => onChange(helpers.deleteSchemaProperty(key)(schema))}
								onChange={(key, s) => onChange(helpers.setSchemaProperty(key)(s, schema))}
								schema={schema}
								setInvalidKeys={setInvalidKeys}
								invalidKeys={invalidKeys}
								showAllProperties={showAllProperties}
								hasChanged={hasChanged}
							/>
						</SchemaArrayItemsObjectWrapper>
					</SchemaBox>
				)}

				{helpers.isSchemaArray(schema) && (
					<SchemaBox height={22}>
						<SchemaArrayItems
							schema={helpers.getSchemaItems(schema)}
							onChange={(s) => onChange(helpers.setSchemaItems(s, schema))}
							invalidKeys={invalidKeys}
							setInvalidKeys={setInvalidKeys}
							showAllProperties={showAllProperties}
							hasChanged={hasChanged}
						/>
					</SchemaBox>
				)}

				{helpers.isSelectSchema(schema) && (
					<SchemaBox>
						<SchemaSelectOptions
							schemakey={schemakey ?? ""}
							schema={schema}
							invalidKeys={invalidKeys}
							setInvalidKeys={setInvalidKeys}
							onChange={() => {
								onChange(helpers.setSchemaTypeAndRemoveWrongFields("oneOf", schema));
							}}
						/>
					</SchemaBox>
				)}
			</CollapsibleWrapper>
		</SchemaArrayControlsWrapper>
	);
};

interface SelectProps {
	schema: Schema;
	onChange: () => void;
	schemakey: string;
	setInvalidKeys?: React.Dispatch<React.SetStateAction<string[]>>;
	invalidKeys?: string[];
}

const SchemaSelectOptions: React.FC<SelectProps> = ({ schema, onChange, schemakey, setInvalidKeys, invalidKeys }) => (
	<SelectSchemaProperties
		onChange={onChange}
		schemakey={schemakey}
		schema={schema}
		setInvalidKeys={setInvalidKeys}
		invalidKeys={invalidKeys}
	/>
);

interface ObjectPropertiesProps {
	schema: Schema;
	onDelete: (key: string) => void;
	onChangeKey: (oldKey: string, newKey: string) => void;
	onChange: (key: string, schema: Schema) => void;
	setInvalidKeys?: React.Dispatch<React.SetStateAction<string[]>>;
	invalidKeys?: string[];
	schemaKeys?: string[];
	showAllProperties: boolean;
	hasChanged: boolean;
}

const SchemaObjectProperties: React.FC<ObjectPropertiesProps> = ({
	schema,
	onChangeKey,
	onDelete,
	onChange,
	setInvalidKeys,
	invalidKeys,
	schemaKeys,
	showAllProperties,
	hasChanged,
}) => {
	const { liveRegionRef, announce } = useAnnouncer();

	return (
		<>
			<LiveRegion ref={liveRegionRef} />
			<ul>
				{_.entries(helpers.getSchemaProperties(schema)).map(([key, s], index, allEntries) => (
					<li key={key} aria-label={`Property: ${key}`} style={{ listStyleType: "none" }}>
						<SchemaCreator
							schema={s as Schema}
							schemakey={key}
							isRequired={helpers.isFieldRequired(key)(schema)}
							onDelete={() => {
								onDelete(key);
								announce(`Property ${key} removed`);
							}}
							onChangeKey={(newKey) => onChangeKey(key, newKey)}
							onChange={(newSchema) => onChange(key, newSchema)}
							setInvalidKeys={setInvalidKeys}
							invalidKeys={invalidKeys}
							schemaKeys={schemaKeys}
							showAllProperties={showAllProperties}
							hasChanged={hasChanged}
						/>
					</li>
				))}
			</ul>
		</>
	);
};

interface SelectSchemaProps {
	schema: Schema;
	schemakey: string;
	/** Simplified callback — callers already know which schema to update */
	onChange: () => void;
	setInvalidKeys?: React.Dispatch<React.SetStateAction<string[]>>;
	invalidKeys?: string[];
}

const SelectSchemaPropertiesLayout = styled.div`
	display: flex;
`;

const SelectSchemaPropertiesWrapper = styled.ul`
	display: grid;
`;

const SelectSchemaPropertiesItems = styled.div`
	display: flex;
	gap: 1rem;
`;

const SvgWrapper = styled.div`
	height: auto;
	width: 3rem;
`;

const Svg = styled.svg.attrs({
	"aria-hidden": "true",
})`
	width: 1.5rem;
	height: 100%;
	overflow: initial;
`;

const OptionsWrapper = styled.li`
	display: flex;
`;

const SelectSchemaProperties: React.FC<SelectSchemaProps> = ({
	schema,
	schemakey,
	onChange,
	setInvalidKeys,
	invalidKeys,
}) => {
	const rows = React.useMemo(() => (schema.oneOf ?? []) as any[], [schema.oneOf]);
	const [height, setHeight] = React.useState<number | undefined>(undefined);
	const rowRef = React.useRef<HTMLDivElement>(null);
	const { liveRegionRef, announce } = useAnnouncer();

	React.useEffect(() => {
		if (rowRef.current) {
			setHeight(rowRef.current.clientHeight);
		}
	}, []);

	const handleDeleteOption = React.useCallback(
		(index: number) => {
			if (!Array.isArray(schema.oneOf)) return;

			const item = rows[index];
			const label = item?.title ?? item?.const ?? `option ${index + 1}`;

			// Immutably remove the item
			schema.oneOf = [...schema.oneOf.slice(0, index), ...schema.oneOf.slice(index + 1)];

			announce(`Option ${label} removed`);
			onChange();
		},
		[rows, schema, onChange, announce]
	);

	const handleTitleChange = React.useCallback(
		(index: number, value: string) => {
			if (!Array.isArray(schema.oneOf)) return;
			schema.oneOf[index] = { ...schema.oneOf[index], title: value };
			onChange();
		},
		[schema, onChange]
	);

	const handleConstChange = React.useCallback(
		(index: number, value: string) => {
			if (!Array.isArray(schema.oneOf)) return;

			const isDuplicate = schema.oneOf.some((e: any, i: number) => i !== index && e.const === value);

			if (!isDuplicate) {
				schema.oneOf[index] = { ...schema.oneOf[index], const: value };
				setInvalidKeys && setInvalidKeys([]);
			} else {
				setInvalidKeys && setInvalidKeys(invalidKeys ? [...invalidKeys, value] : [value]);
			}
			onChange();
		},
		[schema, onChange, setInvalidKeys, invalidKeys]
	);

	return (
		<SelectSchemaPropertiesLayout>
			<LiveRegion ref={liveRegionRef} />
			<SelectSchemaPropertiesWrapper>
				{rows.map((item, index) => (
					<OptionsWrapper key={item?.const ?? index}>
						<SvgWrapper>
							<Svg height={height}>
								<line stroke={colourNeutral7} strokeWidth={1} x1="-13" y1="60%" x2="19" y2="60%" />
							</Svg>
						</SvgWrapper>
						<div ref={index === 0 ? rowRef : undefined} role="group" aria-label={`Option ${index + 1}`}>
							<strong>
								<StyledOptionLabel>
									<p>Option {index + 1}.</p>
									<DeleteButton onClick={() => handleDeleteOption(index)} title={`delete option ${index + 1}`} />
								</StyledOptionLabel>
							</strong>
							<SelectSchemaPropertiesItems>
								<Input
									id={`option-title-${index}`}
									placeholder="Title"
									label="Title"
									type="text"
									value={item.title ?? ""}
									onChange={(userInput: string) => handleTitleChange(index, userInput)}
									dataTestid={`select-option-title-${schemakey}-${index}`}
								/>
								<Input
									id={`option-value-${index}`}
									placeholder="Value"
									label="Value"
									type="text"
									value={item.const ?? ""}
									invalidKeys={invalidKeys}
									onChange={(userInput: string) => handleConstChange(index, userInput)}
									dataTestid={`select-option-value-${schemakey}-${index}`}
								/>
							</SelectSchemaPropertiesItems>
						</div>
					</OptionsWrapper>
				))}
			</SelectSchemaPropertiesWrapper>
		</SelectSchemaPropertiesLayout>
	);
};
